// Danh bạ khách quen trên form tạo đơn (tab "Khách quen"): tìm theo tên / SĐT, khách vừa đặt / vừa lưu
// lên đầu; bấm một khách là điền tên, SĐT, địa chỉ giao vào form.

import { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { Loader2, MapPin, Search } from "lucide-react"
import { addressLine } from "@shoptrack/common/customer"
import { get } from "@/lib/api"
import { phoneDisplay } from "@/lib/format"
import { useDebounced } from "@/hooks/useDebounced"
import { cn } from "@/lib/utils"
import type { CustomerRow } from "./types"

export function CustomerContactBook({
  onPick,
  picking,
  onUseNew,
}: {
  onPick: (id: string) => void
  /** id khách đang tải chi tiết để điền */
  picking: string | null
  /** Chuyển sang nhập khách lạ */
  onUseNew: () => void
}) {
  const [q, setQ] = useState("")
  const query = useDebounced(q.trim(), 250)
  const { data, isLoading } = useQuery({
    queryKey: ["customers", "book", query],
    queryFn: () => get<{ customers: CustomerRow[] }>(`/customers?filter=regular&limit=40${query ? `&q=${encodeURIComponent(query)}` : ""}`),
    staleTime: 30_000,
  })
  const rows = data?.customers ?? []

  return (
    <div className="overflow-hidden rounded-lg border border-rule bg-paper">
      <label className="flex items-center gap-2 border-b border-rule px-3 focus-within:bg-raise">
        <Search className="size-4 shrink-0 text-muted" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
          placeholder="Tên hoặc số điện thoại"
          aria-label="Tìm khách quen"
          autoFocus
          className="h-11 desktop:h-10 w-full border-0 bg-transparent text-base sm:text-sm text-ink shadow-none outline-none ring-0 placeholder:text-muted focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0"
        />
      </label>
      <ul className="max-h-[min(18rem,45dvh)] divide-y divide-rule overflow-y-auto overscroll-contain" aria-label="Danh bạ khách quen">
        {isLoading ? (
          <li className="px-3 py-4 text-center text-xs text-muted">Đang tải danh bạ…</li>
        ) : rows.length === 0 ? (
          <li className="space-y-1 px-3 py-4 text-center text-xs text-muted">
            <p>{query ? `Không có khách quen nào khớp "${query}".` : "Chưa có khách quen."}</p>
            <button type="button" onClick={onUseNew} className="cursor-pointer font-medium text-accent hover:underline">
              Nhập khách lạ
            </button>
          </li>
        ) : (
          rows.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                onClick={() => onPick(c.id)}
                disabled={Boolean(picking)}
                className={cn(
                  "flex w-full cursor-pointer items-center gap-3 px-3 py-2 text-left hover:bg-accent/5 focus-visible:bg-accent/5 focus-visible:outline-none disabled:cursor-wait",
                  picking === c.id && "bg-accent/10",
                )}
              >
                <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-accent/10 text-sm font-semibold text-accent">
                  {picking === c.id ? <Loader2 className="size-4 animate-spin" /> : (c.name.trim()[0] ?? "?").toUpperCase()}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-baseline gap-2">
                    <span className="truncate text-sm font-medium text-ink">{c.name}</span>
                    <span className="shrink-0 text-xs tabular-nums text-inkSoft">{phoneDisplay(c.phone)}</span>
                  </span>
                  {c.defaultAddress && (
                    <span className="flex min-w-0 items-center gap-1 text-xs text-muted">
                      <MapPin className="size-3 shrink-0" />
                      <span className="truncate">{addressLine(c.defaultAddress)}</span>
                    </span>
                  )}
                </span>
                <span className="flex shrink-0 flex-col items-end gap-0.5 text-xs">
                  <span className="text-inkSoft">{c.orderCount} đơn</span>
                  {c.boomCount > 0 && <span className="rounded-full bg-danger/10 px-1.5 py-0.5 font-medium text-danger">Boom {c.boomCount}</span>}
                </span>
              </button>
            </li>
          ))
        )}
      </ul>
    </div>
  )
}
